// React
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, NavLink } from 'react-router-dom';

import styled from '@emotion/styled';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { Button, CircularProgress, Box } from '@mui/material';

import AddIcon from '@mui/icons-material/Add';

import { breadcrumbsActions } from '@store/breadcrumbs';
import { usePermissions } from '@store/auth';
import {
  getLOBs,
  paymentOptionsMerchantsActions,
  selectLOBList,
} from '../../store/payment-options-merchants';

import { LOB_ADD_ROUTE, viewLOBs } from '../../payment-options.breadcrumbs';
import { getLobLogo } from '../../../../utils/utils';

const Wrapper = styled(Box)`
  text-align: center;
  margin-top: 35vh;
`;

const ItemWrapper = styled(NavLink)`
  display: flex;
  box-shadow: 4px 4px 8px #d9d9d9;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  box-sizing: border-box;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 10px;
  margin: 10px;
  min-height: 120px;
  color: #374151;
  cursor: pointer;
  text-decoration: none;
  height: calc(100% - 20px);

  [class~='title'] {
    word-wrap: break-word;
    word-break: break-word;
    font-weight: 400;
  }

  &:hover {
    font-weight: 500;
    background-color: #e94334;
    color: white;
    box-shadow: 4px 4px 8px #847777;
    border: none;
  }

  [class~='MuiTypography-h6'] {
    font-size: 18px;
  }

  [class~='MuiTypography-subtitle1'] {
    font-size: 11px;
  }
`;

const LogoWrapper = styled.div`
  position: relative;
  top: 5%;
  width: 100%;
  height: 30%;
  display: flex;
  justify-content: center;
  margin: 0 0 12px 0;
`;

const Logo = styled.img`
  max-height: 50px;
`;

function PaymentMerchantsTiled() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const permissions = usePermissions();

  const [isFetchingData, setIsFetchingData] = useState(true);
  const [filteredLobs, setFilteredLobs] = useState([]);

  const lobs = useSelector(selectLOBList);

  // @TODO: can be transferred to a routing service
  useEffect(() => {
    dispatch(breadcrumbsActions.setBreadcrumbs(viewLOBs));
    dispatch(paymentOptionsMerchantsActions.resetSelectedMerchant());
  }, [dispatch]);

  useEffect(() => {
    setFilteredLobs(lobs);
  }, [lobs]);

  useEffect(() => {
    setIsFetchingData(true);
    dispatch(getLOBs()).then(() => {
      setIsFetchingData(false);
    });
  }, [dispatch]);

  const searchItems = useCallback(
    (e) => {
      const filter = e.target.value;
      const filtered = filter
        ? lobs.filter((lob) =>
            Object.values(lob)
              .join('')
              .toLowerCase()
              .includes(filter.toLowerCase()),
          )
        : lobs;

      setFilteredLobs(filtered);
    },
    [lobs],
  );

  if (isFetchingData)
    return (
      <Wrapper>
        <CircularProgress />
      </Wrapper>
    );

  return (
    <>
      <Grid
        container
        sx={{ padding: '10px' }}
        justifyContent="space-between"
        alignItems="center">
        <Grid xs={4}>
          <TextField
            fullWidth
            placeholder="Search LOB"
            type="search"
            onChange={searchItems}
          />
        </Grid>
        <Grid
          xs={3}
          sx={{ alignSelf: 'right' }}>
          <Button
            disabled={!permissions?.canEditPaymentOptions}
            fullWidth
            variant="contained"
            sx={{ height: '56px' }}
            size="large"
            onClick={() => navigate(LOB_ADD_ROUTE)}
            startIcon={<AddIcon />}>
            Add new LOB
          </Button>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        justifyContent="flex-start">
        {filteredLobs.map((lob) => (
          <Grid
            item
            key={lob.lobName}
            xs={4}
            md={2}
            textAlign="center">
            <ItemWrapper to={`${lob.lobId}`}>
              <LogoWrapper>
                <Logo
                  src={getLobLogo(lob.lobCode)}
                  alt={lob.lobName}
                />
              </LogoWrapper>
              <Typography
                className="title"
                gutterBottom
                component="div">
                {lob.lobName}
              </Typography>
            </ItemWrapper>
          </Grid>
        ))}
      </Grid>
    </>
  );
}

export default PaymentMerchantsTiled;
